import React, { useState, useRef, useEffect } from "react"; 
import marubitLogo from "../../assets/images/marubit_logo.png";
import { useNavStore } from "../../store/navStore";
import { getApiUrl } from "../../api";
import { useAuthStore } from "../../store/authStore";
import { usePoints } from "../../hooks/usePoints";
import { useNavigate } from "react-router-dom";

export default function Header() {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { isLoggedIn, name, role, uniqueMemberNo, clearAuth } = useAuthStore();
  const points = usePoints();
  const navigate = useNavigate();
  const { navItems } = useNavStore() 

  // useEffect 추가
useEffect(() => {
  const checkToken = () => {
    const token = localStorage.getItem('access_token')
    if (!token) {
      if (isLoggedIn) clearAuth()
      return
    }
    // JWT payload 디코딩해서 만료 확인
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      const exp = payload.exp * 1000
      if (Date.now() > exp) {
        clearAuth()
      }
    } catch {
      clearAuth()
    }
  }

  checkToken() // 즉시 체크
  const interval = setInterval(checkToken, 10000) // 10초마다 체크
  return () => clearInterval(interval)
}, [isLoggedIn, clearAuth])


  const handleMouseEnter = (label: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpenMenu(label);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setOpenMenu(null), 150);
  };

  const currentPath = window.location.pathname;

const isActive = (item: (typeof navItems)[0]) => {
  if (item.href) return currentPath === item.href;
  if (item.children) return item.children.some((c) => c.href && currentPath === c.href);
  return false;
};

  const handleLogout = async () => {
    const token = localStorage.getItem("access_token");
    try {
      await fetch(getApiUrl("/marubit/api/member/logout"), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {}
    clearAuth();
    navigate("/");
  };

  const maskName = (name: string) => {
    if (name.length <= 1) return name;
    if (name.length === 2) return name[0] + "*";
    return name[0] + "*".repeat(name.length - 2) + name[name.length - 1];
  };


  return (
    <header className="bg-white shadow-[0_2px_8px_rgba(0,0,0,0.07)]">

      {/* 상단 로고 + 버튼 */}
      <div className="border-b border-gray-100">
        <div className="max-w-[1200px] mx-auto px-6 flex items-center justify-between py-[30px]">

          {/* 로고 */}
          <a href="/" className="inline-flex items-center no-underline">
            <img src={marubitLogo} alt="서천 커뮤니티센터 마루빛" className="h-[60px] w-auto" />
          </a>

          {/* 우측 */}
          <div className="flex items-center gap-3">
            {isLoggedIn ? (
              <>
                {/* 이름 뱃지 — 연보라 */}
                <div className="inline-flex items-center gap-[6px] bg-[#f5f2f0] rounded-[6px] px-[10px] py-[5px]">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#887267" strokeWidth={2.2}>
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
                  </svg>
                  <span className="text-[13px] font-medium text-[#5a4038] whitespace-nowrap">
                    {name ? `${maskName(name)}님` : "회원님"}
                  </span>
                </div>

                {/* 회원번호 뱃지 */}
                {uniqueMemberNo && (
                  <div className="inline-flex items-center gap-[6px] bg-[#f5f2f0] rounded-[6px] px-[10px] py-[5px]">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#887267" strokeWidth={2.2}>
                      <rect x="3" y="5" width="18" height="14" rx="2" />
                      <path d="M7 9h4M7 13h2" />
                    </svg>
                    <span className="text-[13px] font-medium text-[#5a4038] whitespace-nowrap">
                      회원번호 {uniqueMemberNo}
                    </span>
                  </div>
                )}

                {/* 포인트 뱃지 — 노란 (클릭 시 포인트 내역으로 이동) */}
                <button
                  type="button"
                  onClick={() => navigate("/mypage/point")}
                  title="마루 포인트 내역 보기"
                  className="inline-flex items-center gap-[5px] bg-[#fff7e6] rounded-[6px] px-[10px] py-[5px] border-none cursor-pointer transition-colors duration-150 hover:bg-[#ffefcc]"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#b36b00" strokeWidth={2.2}>
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 7v5l3 3" />
                  </svg>
                  <span className="text-[13px] font-medium text-[#7a4800] whitespace-nowrap">
                    {points != null ? `${points.toLocaleString()} 마루` : "… 마루"}
                  </span>
                </button>

                {/* 구분선 */}
                <div className="w-px h-[18px] bg-gray-200" />

                {/* 마이페이지 or 관리자페이지 */}
                <a
                  href={role === 1 ? "/admin" : "/mypage"}
                  onClick={() => sessionStorage.removeItem("mypage_verified")}
                  className="inline-flex items-center gap-1 text-[13px] text-gray-500 no-underline hover:text-[#887267] transition-colors duration-150"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}>
                    <rect x="3" y="3" width="7" height="7" rx="1" />
                    <rect x="14" y="3" width="7" height="7" rx="1" />
                    <rect x="3" y="14" width="7" height="7" rx="1" />
                    <rect x="14" y="14" width="7" height="7" rx="1" />
                  </svg>
                  {role === 1 ? "관리자페이지" : "마이페이지"}
                </a>

                {/* 로그아웃 */}
                <button
                  onClick={handleLogout}
                  className="inline-flex items-center gap-1 text-[13px] text-gray-400 bg-transparent border-none cursor-pointer hover:text-red-400 transition-colors duration-150"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}>
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  로그아웃
                </button>
              </>
            ) : (
              <>
                <a
                  href="/auth/login"
                  className="px-4 py-[7px] text-[13px] font-medium text-[#887267] border border-[#887267] rounded-full no-underline bg-transparent transition-all duration-150 hover:bg-[#887267] hover:text-white whitespace-nowrap"
                >
                  로그인
                </a>
                <a
                  href="/auth/register"
                  className="px-4 py-[7px] text-[13px] font-medium text-white bg-[#887267] border border-[#887267] rounded-full no-underline transition-all duration-150 hover:bg-[#6b5248] hover:border-[#6b5248] whitespace-nowrap"
                >
                  회원가입
                </a>
              </>
            )}
          </div>
        </div>
      </div>

      {/* 네비게이션 */}
      <nav className="relative bg-[#887267]">
        <div className="max-w-[1200px] mx-auto px-6">
          <ul className="flex list-none m-0 p-0 justify-center">
            {navItems.map((item) => {
              const active = isActive(item);
              const isOpen = openMenu === item.label;
              const hasChildren = !!item.children?.length;

              return (
                <li
                  key={item.label}
                  className="relative text-center"
                  style={{ minWidth: '160px' }}
                  onMouseEnter={() => handleMouseEnter(item.label)}
                  onMouseLeave={handleMouseLeave}
                >
                  <a
                    href={item.href ?? "#"}
                    className={[
                      "flex items-center justify-center gap-1 px-2 py-[14px] text-[15px] no-underline transition-colors duration-150 whitespace-nowrap",
                      active
                        ? "font-bold text-white bg-[#6b5248]"
                        : "font-medium text-white/90 hover:text-white hover:bg-[#6b5248]",
                    ].join(" ")}
                  >
                    {item.label}
                    {hasChildren && (
                      <svg viewBox="0 0 24 24" width={12} height={12} fill="none" stroke="currentColor" strokeWidth={2.5}>
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    )}
                  </a>

                  {hasChildren && isOpen && (
                    <ul className="absolute top-full left-1/2 -translate-x-1/2 bg-white shadow-[0_4px_16px_rgba(0,0,0,0.12)] rounded-[0_4px_4px_4px] list-none m-0 py-[6px] min-w-[160px] z-[100]">
                      {item.children!.map((sub) => (
                        <li key={sub.label}>
                          <a
                            href={sub.href ?? "#"}
                            className="block px-[18px] py-[9px] text-[14px] text-[#333] no-underline whitespace-nowrap transition-all duration-100 hover:bg-[#fdf8f0] hover:text-[#887267]"
                          >
                            {sub.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </nav>
    </header>
  );
}